import cn from 'classnames'
import { observer } from 'mobx-react'
import React from 'react'
import { useErrorBoundary } from 'react-error-boundary'

import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import {
   ErrorBoundaryWithCTAType,
   FallBackErrorBoundaryType
} from '@flowwai-frontend/libs/shared/types'

import { useCustomT } from '../../hooks/useCustomT'

import * as Styles from './styles'

interface Props {
   containerClassName?: string
   errorConfig?: FallBackErrorBoundaryType
   shouldShowActionItem?: boolean

   onClickActionItem?: (resetErrorBoundary: () => void) => void
}

const ErrorBoundaryView = (props: Props): React.ReactElement => {
   const {
      containerClassName,
      errorConfig,
      shouldShowActionItem = true
   } = props

   const t = useCustomT('common:common.errorMessages.errorBoundary')

   const { resetBoundary } = useErrorBoundary()

   const shouldRenderActionItem =
      shouldShowActionItem || !!errorConfig?.buttonText

   const onNavigateToHome = (): void => {
      window.location.href = '/'
   }

   const onClickActionItem = (): void => {
      const hasButtonText = !!errorConfig?.buttonText

      const config = errorConfig as ErrorBoundaryWithCTAType

      const hasOnClickAction = hasButtonText && !!config.onClickActionItem

      switch (true) {
         case hasOnClickAction: {
            config?.onClickActionItem?.(resetBoundary)

            break
         }

         case !!props.onClickActionItem: {
            props.onClickActionItem?.(resetBoundary)

            break
         }

         default: {
            onNavigateToHome()
         }
      }
   }

   const renderActionItem = (): React.ReactElement => (
      <Button
         hierarchy={Hierarchy.Primary}
         size={'Small'}
         subVariant={SubVariant.Primary}
         onClick={onClickActionItem}
      >
         {errorConfig?.buttonText ?? t('buttonText')}
      </Button>
   )

   const renderDescriptionText = (): React.ReactElement => (
      <span className={cn(Styles.errorDescriptionTextStyles)}>
         {errorConfig?.description ?? t('descriptionText')}
      </span>
   )

   const renderHeaderText = (): React.ReactElement => (
      <p className={cn(Styles.errorHeaderTextStyles)}>
         {errorConfig?.title ?? t('headerText')}
      </p>
   )

   const renderHeaderWithDescription = (): React.ReactElement => (
      <div className={Styles.headerWithDescriptionContainerStyles}>
         {renderHeaderText()}
         {renderDescriptionText()}
      </div>
   )

   const renderImage = (imageUrl: string): React.ReactElement => (
      <img
         className={cn(Styles.errorImageStyles)}
         src={imageUrl}
         alt={errorConfig?.title}
      />
   )

   return (
      <div className={cn(Styles.containerStyles, containerClassName)}>
         {errorConfig?.imageUrl ? renderImage(errorConfig.imageUrl) : null}
         {renderHeaderWithDescription()}
         {shouldRenderActionItem ? renderActionItem() : null}
      </div>
   )
}

export default observer(ErrorBoundaryView)
